/**
 * correctionChallenge — de quoi patienter pendant que Sam corrige : le « Défi éclair », ou une pause.
 *
 * La logique de l'ancien `PendingAction.js`, sans écran : une seule forme par copie, tirée une fois —
 * le défi dès qu'il y a des questions pour l'élève (`correction_wait.questions`), sinon l'une des
 * trois pauses (respiration, vire-langue, anecdote). Le défi ne compte pas de points : la série des
 * bonnes réponses d'affilée, et un score à la fin, sans « Rejouer ». Chaque réponse part au
 * serveur (`answerCorrectionWait`), qui s'en sert pour ne plus reposer ce que l'élève sait, sans que le
 * jeu l'attende.
 */
import { shownChoicesOf } from "../quizChoices"

/** Une question du défi, telle que `finalize` la sert (`correction_wait.questions`). */
export type ApiChallengeQuestion = {
    id?: number | null
    question?: string | null
    /** La question avec ses formules en LaTeX ; absente quand elle n'en a pas. */
    display_question?: string | null
    choices?: string[] | null
    /** Les mêmes réponses, rang pour rang, avec leurs formules ; absentes sans formule. */
    display_choices?: string[] | null
    correct_index?: number | null
}

/** Les réponses telles que l'élève les voit ; la réponse envoyée reste celle de `choices`, au même rang. */
export function shownChoices(question: ApiChallengeQuestion): string[] {
    return shownChoicesOf(question.choices ?? [], question.display_choices)
}

export type CorrectionGameKind = "quiz" | "breathing" | "tongue" | "fact"

const BREAKS: CorrectionGameKind[] = ["breathing", "tongue", "fact"]

/** Le tirage, une fois par copie : le défi s'il y a des questions jouables, sinon une pause au hasard. */
export function pickCorrectionGame(
    questions: ApiChallengeQuestion[] | null | undefined,
    random: () => number = Math.random
): CorrectionGameKind {
    return playableQuestions(questions).length > 0 ? "quiz" : BREAKS[Math.floor(random() * BREAKS.length)]!
}

/** Les questions qui se jouent : un intitulé, des réponses, une bonne réponse parmi elles. */
export function playableQuestions(questions: ApiChallengeQuestion[] | null | undefined): ApiChallengeQuestion[] {
    return (questions ?? []).filter(
        (q) =>
            !!q?.question &&
            Array.isArray(q.choices) &&
            q.choices.length > 0 &&
            typeof q.correct_index === "number" &&
            q.correct_index >= 0 &&
            q.correct_index < q.choices.length
    )
}

export type ChallengeGame = {
    index: number
    selected: number | null
    streak: number
    bestStreak: number
    right: number
}

export const NEW_GAME: ChallengeGame = { index: 0, selected: null, streak: 0, bestStreak: 0, right: 0 }

export type ChallengeEvent = { type: "ANSWER"; index: number; correctIndex: number } | { type: "NEXT" }

export function challengeReducer(game: ChallengeGame, event: ChallengeEvent): ChallengeGame {
    switch (event.type) {
        case "ANSWER":
            if (game.selected !== null) return game
            if (event.index !== event.correctIndex) return { ...game, selected: event.index, streak: 0 }
            return {
                ...game,
                selected: event.index,
                streak: game.streak + 1,
                bestStreak: Math.max(game.bestStreak, game.streak + 1),
                right: game.right + 1,
            }
        case "NEXT":
            return game.selected === null ? game : { ...game, selected: null, index: game.index + 1 }
        default:
            return game
    }
}
